import { Box } from "rebass/styled-components";
import Heading from "../common/Heading";
import ErrorProp from "../common/ErrorProp";
import { defaultErrorProp } from "../common/ErrorProp";
import useTranslation from "next-translate/useTranslation";
import {
  useSitterContactDispatch,
  useSitterContactState
} from "./SitterContactContext";
import { DateRangePicker, MultipleDatesPicker } from "../datepicker";
import styled from "styled-components";
import { Label } from "@rebass/forms/styled-components";
import {
  DateRange,
  MultipleDates,
  DatesSelectorValue,
  Availability
} from "../../interfaces";
import { DatesFilterMode } from "../../enums";
import {
  getDateFilterModeForCountryServiceType,
  getDateFilterModeForServiceType,
  zonedToUTC
} from "../../lib/utils";
import { eachDayOfInterval } from "date-fns";

export const StyledContactSitterDayPicker = styled(Box)`
  .DayPicker-Day {
    width: 48px !important;
    height: 48px;
    position: relative;
  }

  .DayPicker-NavBar {
    left: 0em;
    right: 0em;
  }

  .DayPicker-Caption {
    margin-bottom: 2em;
    padding-top: 2em;
  }

  .DayPicker-Month {
    min-width: 45%;
  }

  .DayPicker-Day--disabled {
    opacity: 0.5;
  }

  @media (min-width: 1200px) {
    .DayPicker-Day {
      width: 40px !important;
      height: 40px;
    }
  }

  @media (max-width: 768px) {
    .DayPicker-Month {
      margin-left: auto;
      margin-right: auto;
    }

    .DayPicker-Month:last-child {
      display: none;
    }
  }

  .DayPicker-Day--selected:not(.DayPicker-Day--outside) {
    background-color: ${({ theme }) => theme.colors.springGreen};
    color: ${({ theme }) => theme.colors.samoyedWhite};
    border-radius: 0;
  }

  .DayPicker-Day--unavailable:not(.DayPicker-Day--outside) {
    overflow: hidden;
    :before {
      position: absolute;
      top: 8px;
      bottom: 8px;
      left: 50%;
      width: 1px;
      transform: rotateZ(45deg);
      background-color: red;
      content: "";
    }
  }
`;

interface Props {
  error: ErrorProp;
  availability: Availability;
}

export const getDefaultDateSelector = (serviceType): DatesSelectorValue => {
  return {
    kind:
      getDateFilterModeForServiceType(serviceType) == DatesFilterMode.DATE_RANGE
        ? "dateRange"
        : "multipleDates",
    startDate: undefined,
    endDate: undefined,
    dates: []
  };
};

function DatesSelector({ error, availability }: Props) {
  const { t } = useTranslation("sittercontact");

  const sitterContactDispatch = useSitterContactDispatch();
  const sitterContactState = useSitterContactState();
  const { countryServiceType, datesSelectorValue } = sitterContactState;

  const unavailableToUTC = [
    ...availability.booked.map(day => zonedToUTC(day.getTime())),
    ...availability.unavailable.map(day => zonedToUTC(day.getTime()))
  ];

  const onMultipleDatesChange = dates => {
    const payload: DatesSelectorValue = {
      kind: "multipleDates",
      dates: dates,
      startDate: datesSelectorValue.startDate,
      endDate: datesSelectorValue.endDate
    };
    sitterContactDispatch({
      type: "changeDatesSelectorValue",
      payload: { datesSelectorValue: payload }
    });
  };

  const onDateRangeChange = dates => {
    const { from, to } = dates;
    const payload: DatesSelectorValue = {
      kind: "dateRange",
      startDate: from,
      endDate: to,
      dates: datesSelectorValue.dates
    };
    sitterContactDispatch({
      type: "changeDatesSelectorValue",
      payload: { datesSelectorValue: payload }
    });
  };

  const renderDatePicker = () => {
    const disabledDays = [
      {
        before: new Date()
      },
      ...unavailableToUTC
    ];

    const isInvalidDateRange = (from, to) => {
      const range = eachDayOfInterval({
        start: from,
        end: to
      });

      return range.some(day => {
        return unavailableToUTC.some(unavailable => {
          return unavailable.toDateString() == day.toDateString();
        });
      });
    };

    switch (getDateFilterModeForCountryServiceType(countryServiceType)) {
      case DatesFilterMode.MULTIPLE_DATES:
        return (
          <StyledContactSitterDayPicker as="span" data-test="multidate-picker">
            <MultipleDatesPicker
              value={
                datesSelectorValue &&
                (datesSelectorValue as MultipleDates).dates
              }
              onChange={onMultipleDatesChange}
              disabledDays={disabledDays}
              fromMonth={new Date()}
              showOutsideDays={false}
              modifiers={{
                unavailable: unavailableToUTC
              }}
            />
          </StyledContactSitterDayPicker>
        );
      case DatesFilterMode.DATE_RANGE:
        var dateRange = datesSelectorValue as DateRange;
        return (
          <StyledContactSitterDayPicker as="span" data-test="daterange-picker">
            <DateRangePicker
              from={dateRange && dateRange.startDate}
              to={dateRange && dateRange.endDate}
              onChange={onDateRangeChange}
              disabledDays={disabledDays}
              fromMonth={new Date()}
              showOutsideDays={false}
              isInvalidDateRange={isInvalidDateRange}
              modifiers={{
                unavailable: unavailableToUTC
              }}
            />
          </StyledContactSitterDayPicker>
        );
      default:
        throw new Error();
    }
  };

  const renderDateHeader = () => {
    return (
      <Heading as="h3" my={1}>
        {t("datesAndTime")}
      </Heading>
    );
  };

  return (
    <Box>
      {renderDateHeader()}
      <Box
        sx={{
          position: "relative",
          borderBottom: error.hasError ? "1px solid" : "none",
          borderColor: error.hasError ? "errorRed" : ""
        }}
      >
        {renderDatePicker()}
      </Box>
      {error.hasError && (
        <Label
          data-test="dates-selector-error"
          sx={{
            color: "errorRed"
          }}
        >
          {error.message}
        </Label>
      )}
    </Box>
  );
}

DatesSelector.defaultProps = {
  error: defaultErrorProp
};

export default DatesSelector;
